<script setup>
import { ref, watch, computed } from 'vue';
import { message } from 'ant-design-vue';
import { getShopOrderHistory } from '@/api/products/orders';
import formatDateTime from '@/utils/formatDateTime';
import { formatOrderStatusHistoryLogs } from '@/utils/orderStatusHistory';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    orderId: {
        type: [Number, String],
        default: null
    },
    orderNo: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:open']);

const loading = ref(false);
const rawLogs = ref([]);

const modalTitle = computed(() =>
    props.orderNo ? `訂單狀態歷程 — ${props.orderNo}` : '訂單狀態歷程'
);

const timelineItems = computed(() =>
    formatOrderStatusHistoryLogs(rawLogs.value)
);

const loadLogs = async () => {
    if (!props.orderId) {
        rawLogs.value = [];
        return;
    }
    loading.value = true;
    rawLogs.value = [];
    try {
        const res = await getShopOrderHistory(props.orderId);
        rawLogs.value = res?.data || [];
    } catch {
        message.error('載入訂單狀態歷程失敗');
    } finally {
        loading.value = false;
    }
};

watch(
    () => [props.open, props.orderId],
    ([isOpen]) => {
        if (isOpen) {
            loadLogs();
        }
    }
);

const handleClose = () => {
    emit('update:open', false);
};
</script>

<template>
    <a-modal
        :open="open"
        :title="modalTitle"
        :width="520"
        :footer="null"
        :z-index="1100"
        centered
        destroy-on-close
        class="order-status-log-modal"
        @cancel="handleClose"
        @update:open="emit('update:open', $event)"
    >
        <p class="order-status-log-modal__intro">
            以下為依訂單時間自動整理的狀態節點，最新狀態在下方。
        </p>
        <a-spin :spinning="loading">
            <a-empty
                v-if="!loading && !timelineItems.length"
                description="尚無狀態變更紀錄"
                class="order-status-log-modal__empty"
            />
            <a-timeline v-else mode="left" class="order-status-log-timeline">
                <a-timeline-item
                    v-for="(item, index) in timelineItems"
                    :key="item.key"
                    :color="item.color"
                >
                    <div
                        class="status-log-card"
                        :class="{
                            'status-log-card--latest':
                                index === timelineItems.length - 1
                        }"
                    >
                        <div class="status-log-card__head">
                            <a-tag
                                :color="item.color"
                                class="status-log-card__tag"
                            >
                                {{ item.title }}
                            </a-tag>
                            <span class="status-log-card__time">
                                {{ formatDateTime(item.created_at) }}
                            </span>
                        </div>
                        <p v-if="item.hint" class="status-log-card__hint">
                            {{ item.hint }}
                        </p>
                    </div>
                </a-timeline-item>
            </a-timeline>
        </a-spin>
        <div class="order-status-log-modal__footer">
            <a-button type="primary" ghost @click="handleClose">關閉</a-button>
        </div>
    </a-modal>
</template>

<style scoped>
.order-status-log-modal__intro {
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #6a6a7a;
}

.order-status-log-modal__empty {
    margin: 24px 0;
}

.order-status-log-timeline {
    margin-top: 4px;
    padding: 4px 0 0;
}

.order-status-log-timeline :deep(.ant-timeline-item-label) {
    width: 0 !important;
}

.order-status-log-timeline :deep(.ant-timeline-item-tail) {
    border-inline-start-color: rgba(42, 74, 122, 0.35);
}

.status-log-card {
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid rgba(200, 200, 210, 0.45);
    background: rgba(255, 255, 255, 0.95);
}

.status-log-card--latest {
    border-color: rgba(42, 74, 122, 0.45);
    background: linear-gradient(
        135deg,
        rgba(248, 245, 255, 0.95),
        rgba(255, 255, 255, 0.98)
    );
    box-shadow: 0 4px 14px rgba(42, 74, 122, 0.12);
}

.status-log-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.status-log-card__tag {
    margin: 0;
    font-weight: 600;
}

.status-log-card__time {
    font-size: 12px;
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
    white-space: nowrap;
}

.status-log-card__hint {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.55;
    color: #4a4a5a;
}

.order-status-log-modal__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    padding-top: 12px;
    border-top: 1px solid rgba(200, 200, 210, 0.35);
}
</style>
